import {showNotification, Wizard} from 'platform/components';

import {useEffect, useMemo} from 'react';

import {VehicleTypeEnum} from '@dms/api/shared';
import {
  BaseVehicle,
  useVehicleFormContext,
  useWizardTabRouting,
  useFormKeyboardNavigation,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {FullScreenModal} from '@dms/shared';

import {getSafely} from 'shared/web';

import {ServiceBasicInformation} from '../(sections)/ServiceBasicInformation';
import {ServiceFeatures} from '../(sections)/ServiceFeatures';
import {ServiceIntervals} from '../(sections)/ServiceIntervals';
import {ServiceVehicleData} from '../(sections)/ServiceVehicleData';
import {useServiceVehicleWizard} from '../hooks/useServiceVehicleWizard';

interface ModalKeys {
  headline?: string;
  create?: string;
}

interface ServiceVehicleFormProps {
  isCreating: boolean;
  onSave: (values: BaseVehicle) => Promise<void>;
  onDiscard: () => void;
  onVehicleTypeChange: (type: VehicleTypeEnum) => void;
  tabRoutePattern?: keyof typeof vehiclesRoutes;
  modalKeys?: ModalKeys;
  disableVinDecoder?: boolean;
  infoAlert?: {
    title: string;
    message: string;
  };
}

export const ServiceVehicleForm = ({
  isCreating,
  onSave,
  onDiscard,
  onVehicleTypeChange,
  tabRoutePattern = 'createServiceVehicleV2Tab',
  modalKeys = {},
  disableVinDecoder = false,
  infoAlert,
}: ServiceVehicleFormProps) => {
  const {control, formApi, vehicleType} = useVehicleFormContext();

  const tabStepMap = useMemo(
    () => ({
      'basic-information': 0,
      'vehicle-data': 1,
      features: 2,
      'service-intervals': 3,
    }),
    []
  );

  const {wizardRef, onStepChange} = useWizardTabRouting({
    tabStepMap,
    tabRoutePattern: getSafely(vehiclesRoutes, tabRoutePattern),
    defaultTab: 'basic-information',
  });

  const {onKeyDownCapture} = useFormKeyboardNavigation({
    wizardRef,
    onStepChange,
  });

  const wizardSteps = useServiceVehicleWizard(control, {
    basicInformation: (
      <ServiceBasicInformation disableVinDecoder={disableVinDecoder} infoAlert={infoAlert} />
    ),
    vehicleData: <ServiceVehicleData />,
    features: <ServiceFeatures />,
    serviceIntervals: <ServiceIntervals />,
  });

  useEffect(() => {
    onVehicleTypeChange(
      (vehicleType as VehicleTypeEnum | undefined) ??
        ('VEHICLETYPE_PASSENGER_CAR' as VehicleTypeEnum)
    );
  }, [onVehicleTypeChange, vehicleType]);

  return (
    <FullScreenModal
      data-testid="serviceVehicleForm"
      headline={modalKeys.headline ?? i18n.t('entity.vehicle.labels.createServiceVehicle')}
      backgroundColor="general.white"
      scrollable={false}
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isCreating,
          onClick: onDiscard,
        },
        {
          title: modalKeys.create ?? i18n.t('general.actions.create'),
          isLoading: isCreating,
          onClick: () => {
            formApi.handleSubmit(
              (values) => {
                onSave(values);
              },
              (error) => {
                if (i18n.exists('errors.invalid_submit')) {
                  showNotification.warning(i18n.t('errors.invalid_submit'));
                }

                if (process.env.ENV_TYPE === 'dev') {
                  console.error(error);
                }
              }
            )();
          },
        },
      ]}
    >
      <Wizard
        ref={wizardRef}
        orientation="horizontal"
        steps={wizardSteps}
        height="100%"
        onStepChange={onStepChange}
        onKeyDownCapture={onKeyDownCapture}
      />
    </FullScreenModal>
  );
};
